import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Truck, RotateCcw, ChevronRight } from "lucide-react";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [activeColor, setActiveColor] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setSelectedSize(null);
    setActiveImage(0);
    setActiveColor(0);
    fetchProduct(slug).then(setProduct).catch(() => setProduct(false));
  }, [slug]);

  // Images to display depend on whether the product has color variants
  const displayImages =
    product && product.colorVariants && product.colorVariants.length > 0
      ? product.colorVariants[activeColor].images
      : product
      ? product.images
      : [];

  if (product === false) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-3xl">Product not found</p>
        <Link to="/shop" className="mt-4 inline-block ssm-underline-link">
          Back to shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 animate-pulse">
          <div className="aspect-[3/4] bg-neutral-100" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 bg-neutral-100" />
            <div className="h-4 w-1/4 bg-neutral-100" />
            <div className="h-24 bg-neutral-100" />
          </div>
        </div>
      </div>
    );
  }

  const handleAdd = () => {
    if (!selectedSize) {
      toast("Please select a size");
      return;
    }
    const variant = product.colorVariants?.[activeColor] || null;
    addItem(product, selectedSize, quantity, variant);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div data-testid="product-page" className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-neutral-500 mb-8">
        <Link to="/" className="hover:text-black">Home</Link>
        <ChevronRight size={12} />
        <Link to="/shop" className="hover:text-black">Shop</Link>
        <ChevronRight size={12} />
        <span className="text-black">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
        {/* Gallery */}
        <div>
          <div className="aspect-[3/4] bg-[color:var(--ssm-surface)] overflow-hidden flex items-center justify-center">
            {displayImages.length > 0 ? (
              <img
                src={displayImages[activeImage]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            ) : (
              <span className="font-display text-4xl md:text-5xl text-neutral-300 italic px-6 text-center">
                {product.name}
              </span>
            )}
          </div>
          {displayImages.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {displayImages.map((img, i) => (
                <button
                  key={img}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-[3/4] overflow-hidden bg-[color:var(--ssm-surface)] ${
                    activeImage === i ? "ring-1 ring-black" : "opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="md:sticky md:top-28 md:self-start">
          <div className="label-xs mb-3">{product.category}</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium leading-tight mb-3">
            {product.name}
          </h1>
          {product.comingSoon ? (
            <div className="label-xs mb-6 text-neutral-500">Coming soon</div>
          ) : (
            <div className="text-xl mb-6" data-testid="product-price">
              ${product.price.toFixed(2)}
            </div>
          )}

          <p className="text-neutral-600 leading-relaxed mb-8">
            {product.short_description}
          </p>

          {product.comingSoon ? (
            <div
              data-testid="coming-soon-block"
              className="border border-[color:var(--ssm-border)] bg-[color:var(--ssm-surface)] p-6"
            >
              <div className="label-xs mb-2">Arriving soon</div>
              <p className="text-sm text-neutral-700 leading-relaxed">
                This piece is not available for purchase yet. It will be joining the
                collection shortly — check back soon.
              </p>
            </div>
          ) : (
            <>
              {product.colorVariants && product.colorVariants.length > 0 && (
                <div className="mb-6" data-testid="color-selector">
                  <div className="flex items-center justify-between mb-3">
                    <span className="label-xs">
                      Color — <span className="text-neutral-500 normal-case tracking-normal">
                        {product.colorVariants[activeColor].name}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {product.colorVariants.map((c, i) => (
                      <button
                        key={c.name}
                        data-testid={`color-option-${c.name.toLowerCase()}`}
                        onClick={() => {
                          setActiveColor(i);
                          setActiveImage(0);
                        }}
                        aria-label={c.name}
                        className={`w-8 h-8 rounded-full border transition-all ${
                          activeColor === i
                            ? "ring-1 ring-offset-2 ring-black border-transparent"
                            : "border-[color:var(--ssm-border)] hover:border-black"
                        }`}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="label-xs">Size</span>
                  <span className="text-xs text-neutral-500">Size guide</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      data-testid={`size-option-${s}`}
                      onClick={() => setSelectedSize(s)}
                      className={`py-3 text-sm border transition-colors ${
                        selectedSize === s
                          ? "bg-black text-white border-black"
                          : "bg-white text-black border-[color:var(--ssm-border)] hover:border-black"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <div className="label-xs mb-3">Quantity</div>
                <div className="inline-flex items-center border border-[color:var(--ssm-border)]">
                  <button
                    data-testid="qty-decrement"
                    className="px-4 py-2"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  >
                    −
                  </button>
                  <span className="px-6 text-sm" data-testid="qty-value">{quantity}</span>
                  <button
                    data-testid="qty-increment"
                    className="px-4 py-2"
                    onClick={() => setQuantity((q) => q + 1)}
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                data-testid="add-to-cart-btn"
                onClick={handleAdd}
                className="w-full py-4 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors"
              >
                Add to Cart
              </button>
            </>
          )}

          <div className="mt-8 space-y-2 text-xs text-neutral-600">
            <TrustRow icon={Shield} text="Secure encrypted checkout" />
            <TrustRow icon={Truck} text="Free worldwide shipping over $100" />
            <TrustRow icon={RotateCcw} text="30-day hassle-free returns" />
          </div>

          <div className="mt-10 border-t border-[color:var(--ssm-border)] pt-8">
            <h2 className="label-xs mb-3">Description</h2>
            <p className="text-sm leading-relaxed text-neutral-700 whitespace-pre-line">
              {product.description}
            </p>
          </div>

          <div className="mt-8 border-t border-[color:var(--ssm-border)] pt-8">
            <h2 className="label-xs mb-3">Materials &amp; Care</h2>
            <ul className="text-sm text-neutral-700 space-y-1">
              <li>— Designed in-house by Simple Style Mode (ULTRANEQ LLC)</li>
              <li>— Unbranded / own brand product</li>
              <li>— Machine wash cold, tumble dry low</li>
              <li>— Do not bleach, iron on low</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const TrustRow = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-3">
    <Icon size={14} strokeWidth={1.5} />
    <span>{text}</span>
  </div>
);
